'use client';

import { useIFixitApiClient } from '@ifixit/ifixit-api-client';
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as z from 'zod';
import { type User, userKeys } from './helpers';

const RecentNotificationSchema = z.object({
   signature: z.string(),
   url: z.string(),
   message: z.string(),
   date: z.string(),
   icon: z
      .object({
         style: z.enum(['solid', 'regular']),
         name: z.string(),
      })
      .nullable(),
});

const RecentNotificationsSchema = z.object({
   unread: z.number(),
   notifications: z.array(RecentNotificationSchema),
});

export type RecentNotification = z.infer<typeof RecentNotificationSchema>;
type RecentNotifications = z.infer<typeof RecentNotificationsSchema>;

const notificationKeys = {
   recent: ['notifications', 'recent'],
};

/** The newest unread notifications (at most 10) and the unread total, for the header bell. */
export function useRecentNotifications({ enabled }: { enabled: boolean }) {
   const apiClient = useIFixitApiClient();
   const queryClient = useQueryClient();
   return useQuery({
      enabled,
      queryKey: notificationKeys.recent,
      queryFn: async () => {
         const response = await apiClient.get('notifications/recent', {
            statName: 'notifications-recent',
         });
         const recent = RecentNotificationsSchema.parse(response);
         setUserUnreadCount(queryClient, () => recent.unread);
         return recent;
      },
   });
}

type MarkReadOptions = {
   /** Runs once each write finishes, whether or not it succeeded. */
   onSettled?: () => void;
};

export function useMarkNotificationRead({ onSettled }: MarkReadOptions = {}) {
   const apiClient = useIFixitApiClient();
   const queryClient = useQueryClient();
   return useMutation({
      // The API sends the signature already URL-encoded.
      mutationFn: (signature: string) =>
         apiClient.get(`notifications?ids=${signature}`, {
            statName: 'notifications-mark-read',
         }),
      onMutate: async (signature: string) => {
         await queryClient.cancelQueries({ queryKey: notificationKeys.recent });
         queryClient.setQueryData<RecentNotifications>(notificationKeys.recent, recent =>
            recent
               ? {
                    unread: Math.max(0, recent.unread - 1),
                    notifications: recent.notifications.filter(n => n.signature !== signature),
                 }
               : recent
         );
         setUserUnreadCount(queryClient, count => Math.max(0, count - 1));
      },
      onError: () => invalidate(queryClient),
      onSettled,
   });
}

export function useMarkAllNotificationsRead({ onSettled }: MarkReadOptions = {}) {
   const apiClient = useIFixitApiClient();
   const queryClient = useQueryClient();
   return useMutation({
      mutationFn: () => apiClient.delete('notifications/all', 'notifications-mark-all-read'),
      onMutate: async () => {
         await queryClient.cancelQueries({ queryKey: notificationKeys.recent });
         queryClient.setQueryData<RecentNotifications>(notificationKeys.recent, {
            unread: 0,
            notifications: [],
         });
         setUserUnreadCount(queryClient, () => 0);
      },
      onError: () => invalidate(queryClient),
      onSettled,
   });
}

// The PHP pages have no user query; this leaves the cache alone there.
function setUserUnreadCount(queryClient: QueryClient, update: (count: number) => number) {
   queryClient.setQueryData<User | null>(userKeys.user, user =>
      user
         ? { ...user, unread_notification_count: update(user.unread_notification_count ?? 0) }
         : user
   );
}

function invalidate(queryClient: QueryClient) {
   void queryClient.invalidateQueries({ queryKey: notificationKeys.recent });
   void queryClient.invalidateQueries({ queryKey: userKeys.user });
}
